"use client";

import { env } from "@watt/common/config/env";
import {
  FormControl,
  FormItem,
  FormMessage
} from "@watt/ui/components/ui/form";
import Script from "next/script";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { TURNSTILE_LOGIN_ACTION } from "../turnstile-constants";

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      callback?: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
    }
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
};

type TurnstileFieldProps = {
  field: {
    onChange: (value: string) => void;
    value: string;
  };
};

function getTurnstileApi(): TurnstileApi | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return (window as Window & { turnstile?: TurnstileApi }).turnstile;
}

/**
 * AutoForm field that renders an explicit Turnstile widget and writes the
 * token into the form value. Clears + resets after each submit (tokens are
 * single-use).
 */
export function TurnstileAutoFormField({ field }: TurnstileFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const wasSubmittingRef = useRef(false);
  const onChangeRef = useRef(field.onChange);
  const { formState } = useFormContext();

  onChangeRef.current = field.onChange;

  useEffect(() => {
    let isCancelled = false;

    function renderWidget() {
      const turnstile = getTurnstileApi();
      const container = containerRef.current;

      if (!turnstile || !container || widgetIdRef.current) {
        return;
      }

      widgetIdRef.current = turnstile.render(container, {
        action: TURNSTILE_LOGIN_ACTION,
        callback: token => {
          onChangeRef.current(token);
        },
        "error-callback": () => {
          onChangeRef.current("");
        },
        "expired-callback": () => {
          onChangeRef.current("");
        },
        sitekey: env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
      });
    }

    function onScriptReady() {
      if (isCancelled) {
        return;
      }
      renderWidget();
    }

    if (getTurnstileApi()) {
      renderWidget();
    } else {
      window.addEventListener("turnstile-load", onScriptReady);
    }

    return () => {
      isCancelled = true;
      window.removeEventListener("turnstile-load", onScriptReady);

      const turnstile = getTurnstileApi();
      if (turnstile && widgetIdRef.current) {
        turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (wasSubmittingRef.current && !formState.isSubmitting) {
      onChangeRef.current("");
    }
    wasSubmittingRef.current = formState.isSubmitting;
  }, [formState.isSubmitting]);

  useEffect(() => {
    if (field.value) {
      return;
    }

    const turnstile = getTurnstileApi();
    if (turnstile && widgetIdRef.current) {
      turnstile.reset(widgetIdRef.current);
    }
  }, [field.value]);

  return (
    <FormItem className="flex w-full flex-col justify-start">
      <Script
        onLoad={() => {
          window.dispatchEvent(new Event("turnstile-load"));
        }}
        src={TURNSTILE_SCRIPT_SRC}
        strategy="afterInteractive"
      />
      <FormControl>
        <div className="flex justify-center" ref={containerRef} />
      </FormControl>
      <FormMessage />
    </FormItem>
  );
}
